import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { ChangableComponent } from '@app/models/changable.component';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import {
  GameModeEnum,
  GameResults,
  GameScenarioEnum,
  IVocabularyStudyResults,
  Vocabulary as VocabularyContent,
} from 'lingo2-models';
import { takeUntil } from 'rxjs/operators';
import { VocabularyService } from '../../../../core/services';

interface IScenario {
  scenario: GameScenarioEnum;
  title: string;
  active: boolean;
}

const scenarios: IScenario[] = [
  {
    scenario: GameScenarioEnum.opencard,
    title: 'Карточки',
    active: true,
  },
  {
    scenario: GameScenarioEnum.truefalse,
    title: 'Верно/Неверно',
    active: true,
  },
  {
    scenario: GameScenarioEnum.choice_term,
    title: 'Выбрать карточки',
    active: true,
  },
  {
    scenario: GameScenarioEnum.write_term,
    title: 'Письмо',
    active: true,
  },
  {
    scenario: GameScenarioEnum.listen_term,
    title: 'Аудирование',
    active: true,
  },
];

export interface IVocabularyPlaymode {
  mode: GameModeEnum;
  scenario?: GameScenarioEnum;
}

@Component({
  selector: 'app-vocabulary-playmode',
  templateUrl: './vocabulary-playmode.component.html',
  styleUrls: ['./vocabulary-playmode.component.scss'],
})
export class VocabularyPlaymodeComponent extends ChangableComponent implements OnDestroy {
  @Input() set vocabulary(vocabulary: VocabularyContent) {
    this._vocabulary = vocabulary;
    this.loadVocabularyStudy();
  }
  public scenarios = scenarios;
  public cardMouseOverState = {
    classic: false,
    memorizer: false,
    test: false,
    game: false,
  };
  public vocabularyStudy: IVocabularyStudyResults;
  protected _vocabulary: VocabularyContent;
  protected lastFinalTest: GameResults;
  @Output() playmode = new EventEmitter<IVocabularyPlaymode>();
  @Output() options = new EventEmitter<boolean>();

  constructor(
    public errorNotificationService: ErrorNotificationService,
    private vocabularyService: VocabularyService,
    protected readonly cdr: ChangeDetectorRef,
  ) {
    super(cdr);
  }

  public rotateCard(mode: string) {
    this.cardMouseOverState[mode] = !this.cardMouseOverState[mode];
    this.detectChanges();
  }

  protected loadVocabularyStudy() {
    if (!this._vocabulary.id) {
      return;
    }

    this.vocabularyService
      .getVocabularyStudyResults(this._vocabulary.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (results) => {
          this.vocabularyStudy = results;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public get scenariosUsed(): boolean {
    return this.vocabularyStudy && this.vocabularyStudy.scenarios.filter((s) => s.is_used).length > 0;
  }

  public isScenarioStudied(scenario: GameScenarioEnum): boolean {
    const _scenario = this.vocabularyStudy.scenarios.find((s) => s.scenario === scenario);
    return _scenario && _scenario.is_studied;
  }

  public get memorizerUsed(): boolean {
    return this.vocabularyStudy && this.termsStudied > 0;
  }

  public get termsSelected(): number {
    return this.vocabularyStudy ? this.vocabularyStudy.terms.filter((t) => t.is_used).length : 0;
  }

  public get termsStudied(): number {
    return this.vocabularyStudy ? this.vocabularyStudy.terms.filter((t) => t.is_studied).length : 0;
  }

  public get testUsed(): boolean {
    return true; // !!this.lastFinalTest;
  }

  public get testGrade(): string {
    return 'A+';
  }

  public get gameUsed(): boolean {
    return !false;
  }

  public get gameLevel(): number {
    return 4;
  }

  public get gameProgress(): number {
    return 3456;
  }

  public get nextGameLevel(): number {
    return 5;
  }

  public get nextGameLevelProgressDiff(): number {
    return 1234;
  }

  public selectScenarioPlayMode(scenario?: IScenario) {
    if (scenario && !scenario.active) {
      return;
    }
    this.playmode.emit({
      mode: GameModeEnum.scenario,
      scenario: scenario ? scenario.scenario : this.nextScenario,
    });
  }

  public selectMemorizerPlayMode() {
    this.playmode.emit({
      mode: GameModeEnum.memorizer,
    });
  }

  public selectTestPlayMode() {
    this.playmode.emit({
      mode: GameModeEnum.test,
    });
  }

  public selectMultiplayerPlayMode() {
    this.playmode.emit({
      mode: GameModeEnum.multiplayer,
    });
  }

  /**
   * Возвращает первый из неизученных сценариев
   * Если неизученных нет, то самый последний из сценариев
   */
  public get nextScenario(): GameScenarioEnum {
    const activeScenarios = this.scenarios.filter((s) => s.active);
    const items = activeScenarios.filter((s) => !this.isScenarioStudied(s.scenario));
    return items.length ? items[0].scenario : activeScenarios[activeScenarios.length - 1].scenario;
  }

  public trackByFn(index) {
    return index;
  }
}
